import {
  Purchase,
  SUPPLY_UNIT_LABELS,
  type Supply,
  type SupplyPurchaseBody,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field } from '../../components/Form';
import { Combobox } from '../../components/Combobox';
import { supplyOption } from '../../components/pickers';
import { Modal } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useInvalidateInventory } from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import {
  centavosInput,
  decimalInput,
  parseBrDate,
  parseCentavos,
  parseDecimal,
} from '../../lib/numbers';

interface Row {
  key: string;
  supplyId: string;
  qty: string;
  cost: string;
}

const rowFor = (s: Supply): Row => ({
  key: crypto.randomUUID(),
  supplyId: s.id,
  qty: decimalInput(Math.max(1, Math.ceil((s.minimumQuantity ?? 0) * 2 - s.quantityOnHand)), 3, 0),
  cost: centavosInput(s.unitCostCentavos),
});

/** "Registrar reposição" (board Insumos): prefilled with every item below its minimum. */
export function RestockDialog({ supplies, onClose }: { supplies: Supply[]; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateInventory();
  const [idem] = useState(newIdempotencyKey);
  const [supplier, setSupplier] = useState('');
  const [date, setDate] = useState(formatIsoDate(isoDateInSaoPaulo()));
  const [rows, setRows] = useState<Row[]>(() => {
    const low = supplies.filter((s) => s.low);
    return (low.length ? low : supplies.slice(0, 1)).map(rowFor);
  });
  const lines = rows.map((r) => ({
    row: r,
    supply: supplies.find((s) => s.id === r.supplyId),
    qty: parseDecimal(r.qty),
    cost: parseCentavos(r.cost),
  }));
  const valid = lines.filter((l) => l.supply && l.qty && l.qty > 0 && l.cost !== null);
  const total = valid.reduce((n, l) => n + Math.round(l.qty! * l.cost!), 0);
  const iso = parseBrDate(date);
  const save = useMutation({
    mutationFn: () => {
      const body: SupplyPurchaseBody = {
        supplier: supplier.trim() || null,
        purchasedOn: iso!,
        lines: valid.map((l) => ({
          supplyId: l.supply!.id,
          quantity: l.qty!,
          unitCostCentavos: l.cost!,
        })),
      };
      return api.post('/supply-purchases', Purchase, body, { idempotencyKey: idem });
    },
    onSuccess: (p) => {
      invalidate();
      toast(`Reposição registrada: ${p.lines.length} ${p.lines.length === 1 ? 'item' : 'itens'}.`);
      onClose();
    },
  });
  const cols = '2fr 100px 120px 100px 36px';
  return (
    <Modal
      title="Registrar reposição"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              if (!valid.length || !iso) {
                toast(
                  !iso
                    ? 'Confira a data (DD/MM/AAAA).'
                    : 'Adicione pelo menos uma linha com quantidade.',
                );
                return;
              }
              save.mutate();
            }}
          >
            Registrar · {formatBRL(total)}
          </button>
        </>
      }
    >
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <Field id="rs-sup" label="Fornecedor">
          <input
            id="rs-sup"
            className="input"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          />
        </Field>
        <Field id="rs-date" label="Data">
          <input
            id="rs-date"
            className="input"
            inputMode="numeric"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
      </div>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 520 }}>
          <span>Item</span>
          <span className="num">Qtd.</span>
          <span className="num">Custo un.</span>
          <span className="num">Subtotal</span>
          <span />
        </div>
        {lines.map((l) => {
          const set = (p: Partial<Row>) =>
            setRows(rows.map((r) => (r.key === l.row.key ? { ...r, ...p } : r)));
          return (
            <div
              key={l.row.key}
              className="gtr"
              style={{ gridTemplateColumns: cols, minWidth: 520 }}
            >
              <Combobox
                ariaLabel="Item"
                value={l.row.supplyId}
                options={supplies.map(supplyOption)}
                onChange={(id) => {
                  const s = supplies.find((x) => x.id === id);
                  if (s) set({ supplyId: s.id, cost: centavosInput(s.unitCostCentavos) });
                }}
              />
              <AffixInput
                ariaLabel="Quantidade"
                unit={l.supply ? SUPPLY_UNIT_LABELS[l.supply.unit].short : ''}
                value={l.row.qty}
                onChange={(v) => set({ qty: v })}
              />
              <AffixInput
                ariaLabel="Custo unitário"
                prefix="R$"
                value={l.row.cost}
                onChange={(v) => set({ cost: v })}
              />
              <span className="num">
                {l.qty && l.cost !== null ? formatBRL(Math.round(l.qty * l.cost)) : '—'}
              </span>
              <button
                type="button"
                className="btn sm g icon"
                aria-label="Remover linha"
                onClick={() => setRows(rows.filter((r) => r.key !== l.row.key))}
              >
                ×
              </button>
            </div>
          );
        })}
        <div className="gtr foot" style={{ gridTemplateColumns: cols, minWidth: 520 }}>
          <span>Total da compra</span>
          <span />
          <span />
          <span className="num">{formatBRL(total)}</span>
          <span />
        </div>
      </div>
      <button
        type="button"
        className="btn sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => supplies[0] && setRows([...rows, rowFor(supplies[0])])}
      >
        + Linha
      </button>
      <p className="xs muted">
        Soma ao estoque e atualiza o custo unitário de cada item para o preço desta compra. Lança a
        despesa em Insumos (com o frete).
      </p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}
